import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { PlusCircle, BarChart3, LogOut, Trash2, Coins, Shield, AlertTriangle, Tag, Headphones, Bell, BookOpen } from 'lucide-react';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { user, logout } = useAuth();
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadMonitors();
    loadBalance();
    loadNotifications();
  }, []);

  const loadMonitors = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/monitors`, {
        withCredentials: true
      });
      setMonitors(data);
    } catch (error) {
      console.error('Error loading monitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/billing/balance`, { withCredentials: true });
      setCreditBalance(data.balance);
    } catch (e) { /* ignore for unauthenticated */ }
  };

  const loadNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications/my`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  const markNotificationRead = async (nid) => {
    await axios.post(`${API_URL}/api/notifications/read/${nid}`);
    loadNotifications();
  };

  const markAllRead = async () => {
    await axios.post(`${API_URL}/api/notifications/read-all`);
    loadNotifications();
    setShowNotifications(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDelete = async (e, monitorId, name) => {
    e.stopPropagation();
    if (!window.confirm(t('dashboard.удалить_проект_подтверждение', { name }))) {
      return;
    }
    setDeletingId(monitorId);
    try {
      await axios.delete(`${API_URL}/api/monitors/${monitorId}`, { withCredentials: true });
      setMonitors(prev => prev.filter(m => m.id !== monitorId));
    } catch (error) {
      console.error('Error deleting monitor:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('dashboard.статус_ожидание'),
      analyzing: t('dashboard.статус_анализ'),
      completed: t('dashboard.статус_завершено'),
      error: t('dashboard.статус_ошибка')
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-slate-50 border-slate-200 text-slate-600',
      analyzing: 'bg-blue-50 border-blue-200 text-blue-700',
      completed: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      error: 'bg-red-50 border-red-200 text-red-700'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <div className="flex items-baseline gap-1.5">
            <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
            <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
            <span className="hidden sm:inline text-xs text-slate-400 ml-2">{t('dashboard.сервис_слоган')}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Credit balance widget */}
            <button
              onClick={() => navigate('/billing')}
              className="flex items-center gap-2 bg-[#E8E5D6]/60 hover:bg-[#E8E5D6] px-3 py-2 transition-colors"
              data-testid="credit-balance-widget"
            >
              <Coins className="w-4 h-4 text-[#1BB594]" />
              <span className="text-sm font-bold text-slate-900">{creditBalance !== null ? '$' + (creditBalance / 100).toFixed(2) : '...'}</span>
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-[#1BB594]/10 text-[#1BB594] border border-[#1BB594]/30 hover:bg-[#1BB594]/20"
                data-testid="admin-panel-link"
              >
                <Shield className="w-3.5 h-3.5" />
                {t('dashboard.админ')}
              </button>
            )}
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 text-slate-600 border border-[#E2DDD8] hover:bg-slate-50"
              data-testid="pricing-link"
            >
              <Tag className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{t('dashboard.тарифы')}</span>
            </button>
            <button
              onClick={() => navigate('/support')}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 text-slate-600 border border-[#E2DDD8] hover:bg-slate-50"
              data-testid="support-link"
            >
              <Headphones className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{t('dashboard.поддержка')}</span>
            </button>
            <button
              onClick={() => window.open('/user-guide.html', '_blank')}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 text-slate-600 border border-[#E2DDD8] hover:bg-slate-50"
              data-testid="guide-link"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{t('dashboard.инструкция')}</span>
            </button>
            <span className="text-sm text-slate-600 hidden md:inline">{user?.name}</span>
            {/* Notifications bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative flex items-center justify-center w-9 h-9 text-slate-600 border border-[#E2DDD8] hover:bg-slate-50"
                data-testid="notifications-bell"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="unread-badge">{unreadCount}</span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-11 w-96 bg-white border border-[#E2DDD8] shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="notifications-dropdown">
                  <div className="flex items-center justify-between p-3 border-b border-[#E2DDD8]">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t('dashboard.уведомления')}</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-[10px] text-[#1BB594] hover:underline font-bold">{t('dashboard.прочитать_все')}</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="p-4 text-xs text-slate-400 text-center">{t('dashboard.нет_уведомлений')}</p>
                  ) : (
                    notifications.map(n => (
                      <div
                        key={n.id}
                        className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${!n.read ? 'bg-blue-50/50' : ''}`}
                        onClick={() => !n.read && markNotificationRead(n.id)}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <div className="w-2 h-2 rounded-full bg-[#1BB594] mt-1 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800">{n.title}</p>
                            <p className="text-sm text-slate-600 mt-2 whitespace-pre-line leading-relaxed" dangerouslySetInnerHTML={{__html: (n.message || '').replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener" style="color:#1BB594;text-decoration:underline;word-break:break-all">$1</a>')}} />
                            <p className="text-xs text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleDateString(dateLocale) : ''}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="rounded-none font-bold tracking-widest uppercase text-xs px-4 py-2 bg-white border border-[#E2DDD8] hover:bg-slate-50 text-slate-900 transition-transform active:translate-y-1 flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('dashboard.выход')}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-slate-900 mb-2">
              {t('dashboard.ваши_проекты')}
            </h2>
            <p className="text-base leading-relaxed font-normal text-slate-600">
              {t('dashboard.управляйте_анализом')}
            </p>
          </div>
          <button
            onClick={() => navigate('/monitor/new')}
            className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-4 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2"
            data-testid="create-monitor-button"
          >
            <PlusCircle className="w-5 h-5" />
            {t('dashboard.новый_проект')}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl font-semibold text-slate-600">{t('common.загрузка')}</div>
          </div>
        ) : monitors.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-state">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/720e20e2-ca8b-4918-b8cd-1c316b4be901/images/688c896ea9d30a043b229907a45d87e39fc5838259f8a942c9666c4159b94ea9.png"
              alt="Empty state"
              className="w-64 h-64 mx-auto mb-8 opacity-50"
            />
            <h3 className="text-xl sm:text-2xl tracking-tight font-semibold text-slate-900 mb-4">
              {t('dashboard.нет_проектов')}
            </h3>
            <p className="text-base text-slate-600 mb-8">
              {t('dashboard.создайте_первый_проект')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0" data-testid="monitors-grid">
            {monitors.map((monitor) => (
              <div
                key={monitor.id}
                className="bg-white border border-[#E2DDD8] p-6 sm:p-8 hover:bg-slate-50 cursor-pointer transition-colors group"
                onClick={(e) => {
                  if (e.target.closest('[data-delete-btn]')) return;
                  navigate(`/monitor/${monitor.id}`);
                }}
                data-testid={`monitor-card-${monitor.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 break-all">
                      {monitor.company_info?.company_name || monitor.company_url}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {monitor.country}
                    </p>
                  </div>
                  <span className={`rounded-none border px-2 py-1 text-xs font-bold uppercase tracking-widest ${getStatusColor(monitor.status)}`}>
                    {getStatusLabel(monitor.status)}
                  </span>
                </div>

                {monitor.company_info && (
                  <div className="mt-4 pt-4 border-t border-[#E2DDD8]">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                      {t('dashboard.направления')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {monitor.company_info.activities?.slice(0, 3).map((activity, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-slate-100 text-slate-700"
                        >
                          {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {monitor.status === 'error' && monitor.error_code && (
                  <div className="mt-4 pt-4 border-t border-red-200" data-testid={`monitor-error-${monitor.id}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-red-600 mb-1">{monitor.error_code}</p>
                        <p className="text-xs text-red-500">{monitor.error_message}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#1BB594] text-sm font-semibold">
                    <BarChart3 className="w-4 h-4" />
                    {t('dashboard.открыть_проект')}
                  </div>
                  <button
                    data-delete-btn="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDelete(e, monitor.id, monitor.company_info?.company_name || monitor.company_url);
                    }}
                    disabled={deletingId === monitor.id}
                    className="rounded-none text-xs font-bold uppercase tracking-widest px-3 py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`delete-monitor-${monitor.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {deletingId === monitor.id ? '...' : t('dashboard.удалить')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
